import { PageContainer } from '~/components/Page/Page.Container';
import {
  GetAdminUserWithActivityStatusResponse,
  UpdateAdminUserDailyCounterRequest,
  usersApi,
} from '../../Users.api';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { TeamOverViewHeader } from './SupportTeamOverview.Header';
import { Table } from '~/components/Table/Table';
import { CardWithHeader } from '~/components/Card/CardWithHeader';
import { translate } from '~/utils/translation.utils';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import { AppText } from '~/components/Text';
import { SocketInfo } from './SupportTeamOverview.SocketInfo';
import { useIsSystemUser } from '~/hooks/auth.hook';
import { useFormModal } from '~/components/Modals/FormModal';
import { useCallback } from 'react';
import { ConfigForData } from '@droplet-tech-code/core-common-types';
import { AdminUser } from '@naus-code/naus-admin-types';
import { Toggle } from '@droplet-tech-code/core-elements/module/ui/Toggle/Toggle';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';

export const SupportTeamOverviewScreen = () => {
  return (
    <PageContainer>
      <SupportTeamOverviewLayout />
    </PageContainer>
  );
};

export const SupportTeamOverviewLayout = () => {
  const { data, isLoading, refetch } =
    usersApi.useGetCustomerSupportTeamLeadManageesQuery(undefined, {
      pollingInterval: 10000,
    });

  const isSystem = useIsSystemUser();

  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (!data) {
    return null;
  }

  const managees = [...data.managees].sort((a, b) => {
    const x = a.isOnline ? 1 : 0;
    const y = b.isOnline ? 1 : 0;
    return y - x;
  });

  return (
    <VStack>
      <TeamOverViewHeader managees={managees} />
      <VStack p="2.5" space="2.5">
        <TeamOverviewTable refetch={refetch} managees={managees} />
        {isSystem && <SocketInfo />}
      </VStack>
    </VStack>
  );
};

const TeamOverviewTable = ({
  refetch,
  managees,
}: {
  refetch: () => void;
  managees: GetAdminUserWithActivityStatusResponse[];
}) => {
  const tableHeaders = [
    'name',
    'status',
    'New SR',
    'Open SR',
    'Pending SR',
    'Closed SR',
    'Blocked',
  ];

  const tableData = managees.map((managee) => {
    const adminUser = managee.adminUser;
    return [
      adminUser.name + ' ' + adminUser.lastName,
      managee.isOnline ? 'ON' : 'OFF',
      adminUser.pendingSupportRequests,
      adminUser.openSupportRequests,
      adminUser.pausedSupportRequests,
      adminUser.closedSupportRequests,
    ];
  });

  const onlineUsers = managees.filter((managee) => managee.isOnline).length;
  const onlineStatuses = managees.map((managee) => managee.isOnline);
  const dailyCounters = managees.map((managee) => ({
    id: managee.adminUser._id,
    dailyCount: managee.adminUser.dailySupportRequests,
  }));
  const users = managees.map((managee) => managee.adminUser);
  return (
    <VStack>
      <CardWithHeader
        headerText={`${translate('supportTeam.usersCurrentlyOnline')} (${onlineUsers})`}
        iconRightType="refresh"
        iconRightOnPress={refetch}
        w={700}
      >
        <HStack>
          <StatusLightCol onlineStatuses={onlineStatuses} />
          <Table
            data={tableData}
            headers={tableHeaders}
            cellAlign="flex-start"
            rowHeight={16}
          />
          <DailyCol dailyCounters={dailyCounters} />
          <TempBlockCol users={users} />
        </HStack>
      </CardWithHeader>
    </VStack>
  );
};

const StatusLightCol = ({ onlineStatuses }: { onlineStatuses: boolean[] }) => {
  return (
    <VStack p="2.5" key={'color-column'}>
      <VStack py="1" space="2.5">
        <View justify="center" align="center" h={16}>
          <AppText.ExtraSmall align="center" color="monochrome-dark">
            {''}
          </AppText.ExtraSmall>
        </View>
        {onlineStatuses.map((status, index) => {
          const color = status ? 'success-light' : 'monochrome';
          return (
            <View br={20} align="center" justify="center" key={index} h={16}>
              <View bg={color} br={20} key={index} style={{ height: 6, width: 6 }} />
            </View>
          );
        })}
      </VStack>
    </VStack>
  );
};

const DailyCol = ({
  dailyCounters,
}: {
  dailyCounters: { id: string; dailyCount: number }[];
}) => {
  return (
    <VStack p="2.5" key={'color-column'}>
      <VStack py="1" space="2.5">
        <View justify="center" align="center" h={16}>
          <AppText.Small align="center" color="monochrome-dark">
            {'Daily'}
          </AppText.Small>
        </View>
        {dailyCounters.map(({ id, dailyCount }, index) => {
          if (dailyCount === undefined) {
            return <AppText.Small align="center">{'-'}</AppText.Small>;
          }
          return <DailyCounterVal key={index + ''} initialValue={dailyCount} id={id} />;
        })}
      </VStack>
    </VStack>
  );
};

const TempBlockCol = ({ users }: { users: AdminUser[] }) => {
  return (
    <VStack p="2.5" key={'color-column'}>
      <VStack py="1" space="2.5">
        <View justify="center" align="center" h={16}>
          <AppText.Small align="center" color="monochrome-dark">
            {'Block'}
          </AppText.Small>
        </View>
        {users.map((user) => {
          return <TempBlockSwitch user={user} />;
        })}
      </VStack>
    </VStack>
  );
};

const TempBlockSwitch = ({ user }: { user: AdminUser }) => {
  const [tempBlock, { isLoading }] = usersApi.useTempBlockUserMutation();

  return (
    <View key={user._id} justify="center" align="center" h={16}>
      <HStack>
        <Text.Small style={{ lineHeight: 16 }}>{''}</Text.Small>
        <Toggle
          color="primary"
          size="small"
          disabled={isLoading}
          value={!!user.acceptsSupportTempBlock}
          onChange={() =>
            tempBlock({ id: user._id, tempBlock: !user.acceptsSupportTempBlock })
          }
        />
      </HStack>
    </View>
  );
};

const DailyCounterVal = ({ initialValue, id }: { initialValue: number; id: string }) => {
  const { open } = useFormModal();

  const useFormHookHandler = useCallback((): {
    data?: {
      config?: ConfigForData;
      check?: string;
    };
    isLoading: boolean;
  } => {
    return {
      data: {
        config: [
          {
            items: [
              {
                id: 'dailyCounter',
                type: 'number',
                label: 'Daily Counter',
                initialValue: initialValue,
              },
            ],
          },
        ],
      },
      isLoading: false,
    };
  }, []);

  const onConfirm = async ({
    action,
    value,
  }: {
    action: (req: UpdateAdminUserDailyCounterRequest) => void;
    value: { dailyCounter: number };
  }) => {
    return await action({ id, ...value });
  };

  return (
    <View br={20} align="center" justify="center" h={16}>
      <AppText.Small
        onPress={() => {
          open({
            modalTitle: 'Set daily counters',
            useFormHookHandler,
            onConfirm,
            actionHook: usersApi.useSetUserDailyCounterMutation,
          });
        }}
      >
        {initialValue}
      </AppText.Small>
    </View>
  );
};
